import addCompletedAction from './addCompletedAction';
import { getIsViewingInProviderSupported } from './initialization/createAttachmentFullViewStrategy';
import setOngoingActionAndActionMessage from './setOngoingActionAndActionMessage';
import updateIsViewingInProviderSupported from './updateIsViewingInProviderSupported';
import ActionMessageId from '../schema/ActionMessageId';
import ActionType from '../schema/ActionType';
import AttachmentFullViewState from '../schema/AttachmentFullViewState';
import { PerformanceDatapoint } from 'owa-analytics';
import { DatapointStatus } from 'owa-analytics';
import { saveAttachmentsToCloud } from 'owa-attachment-savetocloud';
import setAttachmentOriginalUrl from 'owa-attachment-model-store/lib/actions/setAttachmentOriginalUrl';
import getAttachment from 'owa-attachment-model-store/lib/selectors/getAttachment';
import AttachmentResultCode from 'owa-service/lib/contract/AttachmentResultCode';
import SaveToCloudNotificationPayload from 'owa-service/lib/contract/SaveToCloudNotificationPayload';
import { action } from 'satcheljs/lib/legacy';
import { ClientAttachmentId } from 'owa-client-ids';

export default action('saveToCloud')(async function saveToCloud(
    attachment: AttachmentFullViewState,
    completeHandler: (succeeded: boolean) => void
) {
    const datapoint: PerformanceDatapoint = new PerformanceDatapoint('AttachmentSaveToCloud');
    saveToCloudPreProcess(attachment);
    const notificationHandler = (payload: SaveToCloudNotificationPayload): boolean => {
        const succeeded = payload.ResultCode === AttachmentResultCode.Success;

        saveToCloudPostProcess(attachment, succeeded, succeeded ? payload.Item.Location : null);

        completeHandler(succeeded);

        logSaveToCloudDatapoint(
            datapoint,
            attachment.attachmentId,
            payload.ResultCode,
            DatapointStatus.ServerError,
            payload.Exception
        );
        return true;
    };

    const serviceCallFailureCallback = (
        error: string,
        resultCode: AttachmentResultCode,
        datapointStatus: DatapointStatus
    ): void => {
        saveToCloudPostProcess(attachment, false, null);
        completeHandler(false);
        logSaveToCloudDatapoint(
            datapoint,
            attachment.attachmentId,
            resultCode,
            datapointStatus,
            error
        );
    };

    await saveAttachmentsToCloud(
        attachment.attachmentId,
        null,
        notificationHandler,
        serviceCallFailureCallback
    );
});

function logSaveToCloudDatapoint(
    datapoint: PerformanceDatapoint,
    attachmentId: ClientAttachmentId,
    resultCode: AttachmentResultCode,
    datapointErrorStatus: DatapointStatus,
    exception: string
) {
    const attachmentModel = getAttachment(attachmentId);
    datapoint.addCustomData([attachmentModel.model.Size, resultCode]);

    if (resultCode === AttachmentResultCode.Success) {
        datapoint.end();
    } else {
        datapoint.endWithError(datapointErrorStatus, new Error(exception));
    }
}

export function saveToCloudPreProcess(attachment: AttachmentFullViewState) {
    setOngoingActionAndActionMessage(
        attachment,
        ActionType.SaveToCloud,
        ActionMessageId.SaveToCloudInProgress
    );
}

export function saveToCloudPostProcess(
    attachment: AttachmentFullViewState,
    succeeded: boolean,
    location: string
) {
    if (succeeded) {
        addCompletedAction(attachment, ActionType.SaveToCloud);
        setAttachmentOriginalUrl(attachment.attachmentId, location);
        const isViewingInProviderSupported = getIsViewingInProviderSupported(false, location);
        updateIsViewingInProviderSupported(attachment, isViewingInProviderSupported);
    }
    setOngoingActionAndActionMessage(
        attachment,
        ActionType.None,
        succeeded
            ? ActionMessageId.SaveToCloudCompletedWithIcon
            : ActionMessageId.SaveToCloudFailedWithIcon
    );
}
